import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { ReplaySubject, takeUntil } from 'rxjs';
import { AuthPolicies } from '../../../auth/auth-policies';
import { RepairModeService } from '../../../services/repair-mode.service';
import { RepairModeListItem } from '../../../models/repair-mode-list-item.model';
import { RepairModeFilter } from '../../../models/repair-mode-filter.model';
import { RepairModeFilterForm } from '../../../form-model/repair-mode-filter.form.model';
import { RepairModeSearchTextForm } from '../../../form-model/repair-mode-search-text.form.model';
import { RepairModeGridColumns } from './repair-mode-grid.config';
import { LoaderComponent } from '../../../shared-components/loader/loader.component';
import { APICallStatus } from '../../../models/api-call-status.mode';
import '@maersk-global/mds-components-core/mc-table';
import '@maersk-global/mds-components-core/mc-pagination';
import '@maersk-global/mds-components-core/mc-select';
import '@maersk-global/mds-components-core/mc-option';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-icon';

@Component({
  selector: 'app-repair-mode-grid',
  imports: [CommonModule, RouterModule, LoaderComponent],
  templateUrl: './repair-mode-grid.component.html',
  styleUrl: './repair-mode-grid.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RepairModeGridComponent implements OnInit, AfterViewInit, OnDestroy {
  data: RepairModeListItem[] = [];
  mockDataForLoadingAnimation: { id: number }[] = Array.from({ length: 20 }, (_, i) => ({ id: i }));

  firstPage: number | undefined;
  previousPage: number | undefined;
  currentPage = 1;
  nextPage: number | undefined;
  lastPage: number | undefined;
  totalItemCount: number | undefined;
  pageLimit = 20;
  sort: string | undefined;
  filterParam: RepairModeFilter = {};
  getRepairModeApiCallStatus: APICallStatus = 'NOT_STARTED';

  columns = RepairModeGridColumns;

  @Input({ required: true }) filterFormGroup!: FormGroup<{
    repairModeFilterForm: FormGroup<RepairModeFilterForm>;
    repairModeSearchTextForm: FormGroup<RepairModeSearchTextForm>;
  }>;

  private readonly destroyed$ = new ReplaySubject<boolean>(1);

  constructor(
    private readonly repairModeService: RepairModeService,
    private readonly router: Router,
    public readonly authPolicy: AuthPolicies,
  ) {}

  ngOnInit(): void {
    this.getRepairModeList();
  }

  ngAfterViewInit(): void {
    this.subscribeToFormChanges();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private subscribeToFormChanges(): void {
    this.filterFormGroup.controls.repairModeFilterForm
      .get('applyRepairModeFilter')!
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((applyVal) => {
        if (applyVal === true) {
          this.onApplyFilter();
        }
      });
    this.filterFormGroup.controls.repairModeSearchTextForm
      .get('appliedRepairModeSearchTextFilter')!
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((searchApplied) => {
        if (searchApplied === true) {
          this.onApplyFilter();
        }
      });
  }

  onSortDirectionChange(event: Event): void {
    const customEvent = event as CustomEvent;
    const sortColumn: string = customEvent.detail.column;
    const sortDirection: string = customEvent.detail.direction === 'ascending' ? 'asc' : 'desc';
    if (sortColumn === 'actions') return;
    const apiColumn = sortColumn === 'isActiveDisplay' ? 'isActive' : sortColumn;
    this.sort = `${apiColumn}:${sortDirection}`;
    this.currentPage = 1;
    this.getRepairModeList();
  }

  onPageLimitChange(event: Event): void {
    const customEvent = event as CustomEvent;
    this.currentPage = 1;
    this.pageLimit = customEvent.detail.value;
    this.getRepairModeList();
  }

  onPageChange(event: Event): void {
    const customEvent = event as CustomEvent;
    const changedPage = customEvent.detail as number;
    if (changedPage !== this.currentPage) {
      this.currentPage = changedPage;
      this.getRepairModeList(true);
    }
  }

  onApplyFilter(): void {
    if (this.filterFormGroup.controls.repairModeFilterForm.valid) {
      this.filterParam = this.extractFilterParams();
      this.currentPage = 1;
      this.getRepairModeList();
    }
  }

  onEditClicked(mode: string): void {
    this.router.navigate(['/master-data/repair-mode', mode, 'edit']);
  }

  clearFilter(): void {
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.repairModeSearchTextForm.get('appliedRepairModeSearchTextFilter')?.setValue(true);
    this.filterFormGroup.controls.repairModeFilterForm.get('applyRepairModeFilter')?.setValue(true);
  }

  private extractFilterParams(): RepairModeFilter {
    const filterForm = this.filterFormGroup.controls.repairModeFilterForm;
    const filters: RepairModeFilter = {};

    const codeList = filterForm.get('repairModeCodeList')?.value;
    if (codeList && Array.isArray(codeList) && codeList.length > 0) {
      filters.repairModeCodeList = codeList;
    }

    const activeVal = filterForm.get('activeStatusesList')?.value;
    if (activeVal !== null && activeVal !== undefined && activeVal !== '') {
      filters.activeStatusesList = [activeVal === 'true'];
    }

    const searchText = this.filterFormGroup.controls.repairModeSearchTextForm.get('SearchText')?.value;
    if (searchText) filters.SearchText = searchText;

    return filters;
  }

  getRepairModeList(isPageChange = false): void {
    this.getRepairModeApiCallStatus = 'IN_PROGRESS';
    this.repairModeService
      .getRepairModeList(this.currentPage, this.pageLimit, this.sort, this.filterParam)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          const headers = response.headers;
          this.data = (response.body || []).map((item) => ({
            ...item,
            id: item.mode,
            isActiveDisplay: item.isActive ? 'Y' : 'N',
          }));
          if (headers) {
            this.firstPage = headers.get('First-Page') ? +headers.get('First-Page')! : undefined;
            this.previousPage = headers.get('Previous-Page') ? +headers.get('Previous-Page')! : undefined;
            this.currentPage = +headers.get('Current-Page')! || this.currentPage;
            this.nextPage = headers.get('Next-Page') ? +headers.get('Next-Page')! : undefined;
            this.lastPage = headers.get('Last-Page') ? +headers.get('Last-Page')! : undefined;
            this.totalItemCount = headers.get('Total-Item-Count') ? +headers.get('Total-Item-Count')! : undefined;
          }
          this.getRepairModeApiCallStatus = 'COMPLETED_SUCCESFULLY';
        },
        error: () => {
          this.getRepairModeApiCallStatus = 'FAILED';
          this.data = [];
        },
      });
  }
}
